/**
 * Aspect Admin — shared shell: sidebar, topbar, command palette,
 * drawer/modal/toast primitives, and small composable components.
 */

import { icon } from "./icons.js";
import { escapeHtml } from "./auth_shell.js";
import { getSession, clearSession } from "../auth_store.js";
import { navigate } from "../router.js";
import { t, langToggle } from "../i18n.js";
import { WORKSPACE, NOTIFICATIONS } from "../mock_data.js";

/* ---- Navigation model ------------------------------------------------------- */

export const NAV = [
  {
    section: "Operate",
    items: [
      { path: "/dashboard", label: "Overview", icon: "home" },
      { path: "/spaces", label: "Spaces", icon: "spaces" },
      { path: "/jobs", label: "AI Jobs", icon: "sparkles", count: 4 },
      { path: "/files", label: "Files", icon: "folder" },
    ],
  },
  {
    section: "Grow",
    items: [
      { path: "/analytics", label: "Analytics", icon: "chart" },
      { path: "/clients", label: "Clients", icon: "briefcase" },
      { path: "/activity", label: "Activity", icon: "bell", count: 3 },
    ],
  },
  {
    section: "Manage",
    items: [
      { path: "/team", label: "Team", icon: "users" },
      { path: "/billing", label: "Billing", icon: "card" },
      { path: "/integrations", label: "Integrations", icon: "plug" },
      { path: "/settings", label: "Settings", icon: "gear" },
      { path: "/audit", label: "Audit log", icon: "shield" },
      { path: "/support", label: "Support", icon: "lifebuoy" },
    ],
  },
];

const PAGE_TITLES = {
  "/dashboard": "Overview",
  "/spaces": "Spaces",
  "/jobs": "AI Jobs",
  "/files": "Files",
  "/analytics": "Analytics",
  "/clients": "Clients",
  "/activity": "Activity",
  "/team": "Team",
  "/billing": "Billing",
  "/integrations": "Integrations",
  "/settings": "Settings",
  "/audit": "Audit log",
  "/support": "Support",
};

/* ---- Shell chrome -------------------------------------------------------------- */

function sidebarHtml(activePath, session) {
  const initials = (session.name || session.email)
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const nav = NAV.map(
    (group) => `
      <div class="ap-nav__section">${t(group.section)}</div>
      ${group.items
        .map(
          (it) => `
        <a class="ap-side-item ${it.path === activePath ? "ap-side-item--active" : ""}" href="#${it.path}">
          ${icon(it.icon, 17)}
          <span>${t(it.label)}</span>
          ${it.count ? `<span class="ap-side-item__count">${it.count}</span>` : ""}
        </a>`
        )
        .join("")}
    `
  ).join("");

  return `
    <aside class="ap-sidebar">
      <a class="ap-brand" href="#/dashboard">
        <span class="ap-brand__mark">A</span>
        <span class="ap-brand__text">
          <span class="ap-brand__name">Aspect</span>
          <span class="ap-brand__tag">${t("Admin")}</span>
        </span>
      </a>
      <button type="button" class="ap-workspace" data-shell="workspace">
        <span class="ap-workspace__badge">${WORKSPACE.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("")}</span>
        <span class="ap-workspace__text">
          <span class="ap-workspace__name">${escapeHtml(WORKSPACE.name)}</span>
          <span class="ap-workspace__plan">${t("{p} plan", { p: WORKSPACE.plan })}</span>
        </span>
        ${icon("chevronDown", 14)}
      </button>
      <nav class="ap-nav ap-scroll" aria-label="Main">${nav}</nav>
      <div class="ap-side-foot">
        <div class="ap-side-user">
          <div class="ap-avatar ap-avatar--round">${initials}</div>
          <div class="ap-side-user__text">
            <strong>${escapeHtml(session.name)}</strong>
            <span>${escapeHtml(session.email)}</span>
          </div>
          <button type="button" class="ap-iconbtn ap-iconbtn--bare" data-shell="logout" title="${t("Sign out")}">${icon("logout", 16)}</button>
        </div>
      </div>
    </aside>
  `;
}

function topbarHtml(activePath) {
  const title = t(PAGE_TITLES[activePath] ?? "Overview");
  const unread = NOTIFICATIONS.filter((n) => n.unread).length;
  return `
    <header class="ap-topbar">
      <nav class="ap-crumbs" aria-label="Breadcrumb">
        <span>${escapeHtml(WORKSPACE.name)}</span>
        ${icon("chevronRight", 13)}
        <strong>${title}</strong>
      </nav>
      <button type="button" class="ap-searchbtn" data-shell="palette">
        ${icon("search", 15)}
        <span class="ap-searchbtn__label">${t("Search or run a command…")}</span>
        <kbd class="ap-kbd">⌘K</kbd>
      </button>
      <div class="ap-topbar__group">
        ${langToggle()}
        <button type="button" class="ap-btn ap-btn--ai ap-btn--sm" data-shell="assistant">
          ${icon("sparkles", 15)} ${t("Ask Aspect")}
        </button>
        <div class="ap-menuwrap">
          <button type="button" class="ap-iconbtn" data-shell="notifications" title="${t("Notifications")}">
            ${icon("bell", 17)}
            ${unread ? `<span class="ap-iconbtn__dot"></span>` : ""}
          </button>
          <div class="ap-pop hidden" id="ap-notif-pop">
            <div class="ap-pop__head">
              ${t("Notifications")}
              <a class="ap-btn ap-btn--ghost ap-btn--sm" href="#/activity">${t("View all")}</a>
            </div>
            <div class="ap-feed">
              ${NOTIFICATIONS.slice(0, 4)
                .map(
                  (n) => `
                <div class="ap-feed__item">
                  <div class="ap-feed__icon ${n.kind ? `ap-feed__icon--${n.kind}` : ""}">${icon(n.icon, 15)}</div>
                  <div class="ap-feed__body"><strong>${escapeHtml(t(n.title))}</strong><br />${escapeHtml(t(n.body))}</div>
                  <div class="ap-feed__time">${t(n.time)}</div>
                </div>`
                )
                .join("")}
            </div>
          </div>
        </div>
      </div>
    </header>
  `;
}

/**
 * Wrap a page body in the admin shell and bind global chrome behavior.
 * @param {HTMLElement} root
 * @param {string} activePath
 * @param {string} contentHtml
 */
export function renderShell(root, activePath, contentHtml) {
  const session = getSession();
  if (!session) {
    navigate("/login", true);
    return null;
  }

  root.innerHTML = `
    <div class="ap-shell">
      ${sidebarHtml(activePath, session)}
      <div class="ap-main">
        ${topbarHtml(activePath)}
        <div class="ap-content ap-scroll" id="ap-content">${contentHtml}</div>
      </div>
    </div>
    <div id="ap-overlay-slot"></div>
    <div class="ap-toastwrap" id="ap-toastwrap"></div>
  `;

  bindShell(root);
  return root.querySelector("#ap-content");
}

function bindShell(root) {
  root.querySelector('[data-shell="logout"]')?.addEventListener("click", () => {
    clearSession();
    navigate("/login", true);
  });

  root.querySelector('[data-shell="palette"]')?.addEventListener("click", () => openPalette(root));

  root.querySelector('[data-shell="assistant"]')?.addEventListener("click", () => openAssistant(root));

  root.querySelector('[data-shell="workspace"]')?.addEventListener("click", () => {
    toast(root, t("Workspace switching arrives with multi-workspace support"), "info");
  });

  const notifBtn = root.querySelector('[data-shell="notifications"]');
  const notifPop = root.querySelector("#ap-notif-pop");
  notifBtn?.addEventListener("click", (e) => {
    e.stopPropagation();
    notifPop?.classList.toggle("hidden");
  });

  if (root._shellDocClick) document.removeEventListener("click", root._shellDocClick);
  root._shellDocClick = (ev) => {
    if (!ev.target.closest(".ap-menuwrap")) notifPop?.classList.add("hidden");
    root.querySelectorAll(".ap-pop--menu").forEach((m) => {
      if (!m.parentElement.contains(ev.target)) m.classList.add("hidden");
    });
  };
  document.addEventListener("click", root._shellDocClick);

  if (root._shellKeydown) document.removeEventListener("keydown", root._shellKeydown);
  root._shellKeydown = (ev) => {
    if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === "k") {
      ev.preventDefault();
      openPalette(root);
    }
    if (ev.key === "Escape") closeOverlay(root);
  };
  document.addEventListener("keydown", root._shellKeydown);
}

/* ---- Overlay primitives ----------------------------------------------------------- */

export function closeOverlay(root) {
  const slot = root.querySelector("#ap-overlay-slot");
  if (slot) slot.innerHTML = "";
}

function overlaySlot(root) {
  return root.querySelector("#ap-overlay-slot");
}

/** Slide-over drawer. Returns the drawer element. */
export function openDrawer(root, { title, subtitle = "", bodyHtml, footHtml = "" }) {
  const slot = overlaySlot(root);
  if (!slot) return null;
  slot.innerHTML = `
    <div class="ap-scrim" data-overlay-close></div>
    <aside class="ap-drawer" role="dialog" aria-modal="true" aria-label="${escapeHtml(title)}">
      <div class="ap-drawer__head">
        <div style="flex:1;min-width:0;">
          <h2>${escapeHtml(title)}</h2>
          ${subtitle ? `<p>${subtitle}</p>` : ""}
        </div>
        <button type="button" class="ap-iconbtn ap-iconbtn--bare" data-overlay-close aria-label="${t("Close")}">${icon("x", 17)}</button>
      </div>
      <div class="ap-drawer__body ap-scroll">${bodyHtml}</div>
      ${footHtml ? `<div class="ap-drawer__foot${footHtml.includes("ap-searchfield") ? " ap-drawer__foot--compose" : ""}">${footHtml}</div>` : ""}
    </aside>
  `;
  slot.querySelectorAll("[data-overlay-close]").forEach((el) => el.addEventListener("click", () => closeOverlay(root)));
  return slot.querySelector(".ap-drawer");
}

/** Center modal. Returns the panel element. */
export function openModal(root, { title, bodyHtml, footHtml = "" }) {
  const slot = overlaySlot(root);
  if (!slot) return null;
  slot.innerHTML = `
    <div class="ap-scrim" data-overlay-close></div>
    <div class="ap-modal" role="dialog" aria-modal="true" aria-label="${escapeHtml(title)}">
      <div class="ap-modal__panel">
        <div class="ap-modal__head">
          <h2>${escapeHtml(title)}</h2>
          <button type="button" class="ap-iconbtn ap-iconbtn--bare" data-overlay-close aria-label="${t("Close")}">${icon("x", 17)}</button>
        </div>
        <div class="ap-modal__body ap-scroll">${bodyHtml}</div>
        ${footHtml ? `<div class="ap-modal__foot">${footHtml}</div>` : ""}
      </div>
    </div>
  `;
  slot.querySelectorAll("[data-overlay-close]").forEach((el) => el.addEventListener("click", () => closeOverlay(root)));
  return slot.querySelector(".ap-modal__panel");
}

/* ---- Command palette ------------------------------------------------------------------ */

const COMMANDS = [
  {
    group: "Navigate",
    items: NAV.flatMap((g) =>
      g.items.map((it) => ({ id: `nav:${it.path}`, label: () => t("Go to {x}", { x: t(it.label) }), icon: it.icon, run: () => navigate(it.path) }))
    ),
  },
  {
    group: "Actions",
    items: [
      { id: "act:new-space", label: () => t("Create new space"), icon: "plus", run: (r) => { navigate("/spaces"); toast(r, t("Use “New space” to start a project"), "info"); } },
      { id: "act:invite", label: () => t("Invite team member"), icon: "users", run: () => navigate("/team") },
      { id: "act:apikey", label: () => t("Generate API key"), icon: "key", run: () => navigate("/integrations") },
      { id: "act:invoice", label: () => t("Download latest invoice"), icon: "download", run: (r) => toast(r, t("Invoice INV-2026-0142 downloading…"), "ok") },
      { id: "act:ai", label: () => t("Ask Aspect AI…"), icon: "sparkles", run: (r) => openAssistant(r) },
    ],
  },
];

export function openPalette(root) {
  const slot = overlaySlot(root);
  if (!slot) return;

  const listHtml = (filter = "") => {
    const q = filter.trim().toLowerCase();
    let flatIndex = 0;
    return (
      COMMANDS.map((group) => {
        const items = group.items.filter((c) => !q || c.label().toLowerCase().includes(q));
        if (!items.length) return "";
        return `
        <div class="ap-cmdk__group">${t(group.group)}</div>
        ${items
          .map((c) => {
            const idx = flatIndex++;
            return `<button type="button" class="ap-cmdk__item ${idx === 0 ? "ap-cmdk__item--sel" : ""}" data-cmd="${c.id}">${icon(c.icon, 16)}<span>${escapeHtml(c.label())}</span></button>`;
          })
          .join("")}
      `;
      }).join("") || `<div class="ap-empty" style="padding:2rem 1rem;"><p style="margin:0;">${t("No matching commands.")}</p></div>`
    );
  };

  slot.innerHTML = `
    <div class="ap-scrim" data-overlay-close></div>
    <div class="ap-cmdk">
      <div class="ap-cmdk__panel">
        <div class="ap-cmdk__inputwrap">
          ${icon("command", 17)}
          <input class="ap-cmdk__input" id="ap-cmdk-input" type="text" placeholder="${t("Search pages, actions, spaces…")}" autocomplete="off" spellcheck="false" />
          <kbd class="ap-kbd">esc</kbd>
        </div>
        <div class="ap-cmdk__list ap-scroll" id="ap-cmdk-list">${listHtml()}</div>
      </div>
    </div>
  `;

  const allCmds = COMMANDS.flatMap((g) => g.items);
  const input = slot.querySelector("#ap-cmdk-input");
  const list = slot.querySelector("#ap-cmdk-list");

  const runCmd = (id) => {
    const cmd = allCmds.find((c) => c.id === id);
    closeOverlay(root);
    cmd?.run(root);
  };

  list.addEventListener("click", (e) => {
    const item = e.target.closest("[data-cmd]");
    if (item) runCmd(item.dataset.cmd);
  });

  input.addEventListener("input", () => {
    list.innerHTML = listHtml(input.value);
  });

  input.addEventListener("keydown", (e) => {
    const items = [...list.querySelectorAll(".ap-cmdk__item")];
    const sel = list.querySelector(".ap-cmdk__item--sel");
    const i = items.indexOf(sel);
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = items[(i + (e.key === "ArrowDown" ? 1 : items.length - 1)) % items.length];
      sel?.classList.remove("ap-cmdk__item--sel");
      next?.classList.add("ap-cmdk__item--sel");
      next?.scrollIntoView({ block: "nearest" });
    }
    if (e.key === "Enter" && sel) runCmd(sel.dataset.cmd);
  });

  slot.querySelector("[data-overlay-close]").addEventListener("click", () => closeOverlay(root));
  input.focus();
}

/* ---- AI assistant drawer ----------------------------------------------------------------- */

export function openAssistant(root) {
  const drawer = openDrawer(root, {
    title: t("Aspect Assistant"),
    subtitle: t("Ask about your workspace, jobs, clients, or usage"),
    bodyHtml: `
      <div class="ap-aistrip" style="box-shadow:none;">
        <div class="ap-aistrip__orb">${icon("sparkles", 18)}</div>
        <div class="ap-aistrip__body">
          <h3>${t("Good morning")}</h3>
          <p>${t("The workspace is healthy: <strong>2 jobs running</strong>, queue depth 4, and tour sessions are up <strong>8.1%</strong> this month. What would you like to look into?")}</p>
        </div>
      </div>
      <div class="ap-drawer__section">
        <h4>${t("Suggested")}</h4>
        <div class="ap-stack" style="gap:0.5rem;">
          <button type="button" class="ap-btn" data-ai-q style="justify-content:flex-start;">${t("Why did render volume spike this morning?")}</button>
          <button type="button" class="ap-btn" data-ai-q style="justify-content:flex-start;">${t("Which clients are at churn risk?")}</button>
          <button type="button" class="ap-btn" data-ai-q style="justify-content:flex-start;">${t("Draft a usage summary for the board")}</button>
        </div>
      </div>
      <div id="ap-ai-thread" class="ap-stack" style="gap:0.75rem;"></div>
    `,
    footHtml: `
      <div class="ap-searchfield">
        ${icon("sparkles", 15)}
        <input class="ap-input" id="ap-ai-input" type="text" placeholder="${t("Ask anything about this workspace…")}" autocomplete="off" />
      </div>
      <button type="button" class="ap-btn ap-btn--primary ap-btn--sm" id="ap-ai-send" aria-label="${t("Send")}">${icon("send", 15)}<span class="ap-btn__label">${t("Send")}</span></button>
    `,
  });
  if (!drawer) return;

  const thread = drawer.querySelector("#ap-ai-thread");
  const input = drawer.querySelector("#ap-ai-input");

  const respond = (q) => {
    thread.insertAdjacentHTML(
      "beforeend",
      `<div class="ap-panel ap-panel--pad" style="padding:0.8rem 1rem;font-size:0.83rem;color:var(--ap-ink);font-weight:550;">${escapeHtml(q)}</div>
       <div class="ap-insight">
         <div class="ap-insight__head">${icon("sparkles", 15)}<span class="ap-insight__tag">${t("Assistant")}</span></div>
         <p class="ap-insight__body">${t("This is a demo response. When the AI endpoint is wired, I'll answer with live workspace data — sources and confidence included.")}</p>
         <div class="ap-insight__foot">
           <span class="ap-conf">${t("Confidence")} <span class="ap-conf__bars"><i class="on"></i><i class="on"></i><i></i></span></span>
           <button type="button" class="ap-btn ap-btn--ghost ap-btn--sm">${t("View trace")}</button>
         </div>
       </div>`
    );
    thread.parentElement.scrollTop = thread.parentElement.scrollHeight;
  };

  drawer.querySelectorAll("[data-ai-q]").forEach((b) => b.addEventListener("click", () => respond(b.textContent.trim())));
  const send = () => {
    const q = input.value.trim();
    if (!q) return;
    input.value = "";
    respond(q);
  };
  drawer.querySelector("#ap-ai-send")?.addEventListener("click", send);
  input?.addEventListener("keydown", (e) => e.key === "Enter" && send());
}

/* ---- Toast --------------------------------------------------------------------------------- */

export function toast(root, message, kind = "info") {
  const wrap = root.querySelector("#ap-toastwrap") ?? document.getElementById("ap-toastwrap");
  if (!wrap) return;
  const ic = kind === "ok" ? "check" : kind === "err" ? "alert" : "info";
  const el = document.createElement("div");
  el.className = `ap-toast ${kind === "ok" ? "ap-toast--ok" : kind === "err" ? "ap-toast--err" : ""}`;
  el.setAttribute("role", "status");
  el.innerHTML = `${icon(ic, 15)}<span>${escapeHtml(message)}</span>`;
  wrap.appendChild(el);
  setTimeout(() => {
    el.classList.add("ap-toast--out");
    setTimeout(() => el.remove(), 220);
  }, 2800);
}

/* ---- Small composable pieces ------------------------------------------------------------------ */

export function pageHead(title, sub, actionsHtml = "") {
  return `
    <div class="ap-pagehead">
      <div>
        <h1>${escapeHtml(title)}</h1>
        ${sub ? `<p class="ap-pagehead__sub">${sub}</p>` : ""}
      </div>
      ${actionsHtml ? `<div class="ap-pagehead__actions">${actionsHtml}</div>` : ""}
    </div>
  `;
}

export function statusBadge(status) {
  const map = {
    live: ["ok", "Live"],
    active: ["ok", "Active"],
    paid: ["ok", "Paid"],
    done: ["ok", "Completed"],
    resolved: ["ok", "Resolved"],
    running: ["cyan", "Running"],
    rendering: ["cyan", "Rendering"],
    analyzing: ["cyan", "Analyzing"],
    trial: ["cyan", "Trial"],
    invited: ["cyan", "Invited"],
    queued: ["mute", "Queued"],
    draft: ["mute", "Draft"],
    paused: ["mute", "Paused"],
    pending: ["warn", "Pending"],
    open: ["warn", "Open"],
    attention: ["warn", "Needs attention"],
    "churn-risk": ["err", "Churn risk"],
    failed: ["err", "Failed"],
  };
  const [kind, label] = map[status] ?? ["mute", status];
  const pulse = ["running", "rendering", "analyzing"].includes(status);
  return `<span class="ap-badge ap-badge--${kind}"><span class="ap-dot ${pulse ? "ap-dot--pulse" : ""}"></span>${t(label)}</span>`;
}

export function confidence(value) {
  if (value == null) return `<span class="ap-note">—</span>`;
  const on = value >= 0.85 ? 3 : value >= 0.6 ? 2 : 1;
  return `
    <span class="ap-conf" title="${t("Model confidence {n}%", { n: Math.round(value * 100) })}">
      ${value.toFixed(2)}
      <span class="ap-conf__bars">${[1, 2, 3].map((i) => `<i class="${i <= on ? "on" : ""}"></i>`).join("")}</span>
    </span>
  `;
}

export function meter(label, used, cap, unit = "") {
  const pct = Math.min(100, Math.round((used / cap) * 100));
  const mod = pct >= 92 ? "ap-meter--err" : pct >= 75 ? "ap-meter--warn" : "";
  return `
    <div class="ap-meter ${mod}">
      <div class="ap-meter__head">
        <span class="ap-meter__label">${escapeHtml(t(label))}</span>
        <span class="ap-meter__val">${used.toLocaleString()}${unit ? ` ${unit}` : ""} / ${cap.toLocaleString()}${unit ? ` ${unit}` : ""} · ${pct}%</span>
      </div>
      <div class="ap-meter__track"><div class="ap-meter__fill" style="width:${pct}%"></div></div>
    </div>
  `;
}

export function emptyState({ icon: ic = "layers", title, body, actionHtml = "" }) {
  return `
    <div class="ap-empty">
      <div class="ap-empty__orb">${icon(ic, 24)}</div>
      <h3>${escapeHtml(t(title))}</h3>
      <p>${escapeHtml(t(body))}</p>
      ${actionHtml}
    </div>
  `;
}

export function initials(name) {
  return String(name)
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/* ---- Glass dropdown (replaces native select menus) ------------------------------------ */

let dropdownDocBound = false;

function ensureDropdownDocClose() {
  if (dropdownDocBound) return;
  dropdownDocBound = true;
  document.addEventListener("click", (ev) => {
    if (ev.target.closest(".ap-dropdown")) return;
    document.querySelectorAll(".ap-dropdown__menu").forEach((m) => m.classList.add("hidden"));
    document.querySelectorAll("[data-dropdown-trigger]").forEach((t) => t.setAttribute("aria-expanded", "false"));
  });
}

/** @param {{ value?: string, options: (string|{value:string,label:string})[], disabled?: boolean, className?: string }} opts */
export function dropdownHtml({ value, options, disabled = false, className = "" }) {
  const normalized = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const current = normalized.find((o) => o.value === value) ?? normalized[0];
  return `
    <div class="ap-dropdown ${className}" data-dropdown>
      <button type="button" class="ap-dropdown__trigger" data-dropdown-trigger ${disabled ? "disabled" : ""} aria-haspopup="listbox" aria-expanded="false">
        <span class="ap-dropdown__value">${escapeHtml(current?.label ?? "")}</span>
        ${icon("chevronDown", 12)}
      </button>
      <div class="ap-dropdown__menu hidden" role="listbox">
        ${normalized
          .map(
            (o) => `
          <button type="button" class="ap-dropdown__item ${o.value === current?.value ? "ap-dropdown__item--on" : ""}" data-dropdown-value="${escapeHtml(o.value)}" role="option" aria-selected="${o.value === current?.value}">
            ${o.value === current?.value ? icon("check", 13) : `<span style="width:13px;flex-shrink:0;"></span>`}
            <span>${escapeHtml(o.label)}</span>
          </button>`
          )
          .join("")}
      </div>
    </div>`;
}

/** @param {ParentNode} scope */
export function bindDropdowns(scope, { onChange } = {}) {
  ensureDropdownDocClose();
  scope.querySelectorAll("[data-dropdown]:not([data-dropdown-bound])").forEach((wrap) => {
    wrap.dataset.dropdownBound = "1";
    const trigger = wrap.querySelector("[data-dropdown-trigger]");
    const menu = wrap.querySelector(".ap-dropdown__menu");
    if (!trigger || !menu) return;

    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      if (trigger.disabled) return;
      const wasOpen = !menu.classList.contains("hidden");
      document.querySelectorAll(".ap-dropdown__menu").forEach((m) => m.classList.add("hidden"));
      document.querySelectorAll("[data-dropdown-trigger]").forEach((t) => t.setAttribute("aria-expanded", "false"));
      if (!wasOpen) {
        menu.classList.remove("hidden");
        trigger.setAttribute("aria-expanded", "true");
      }
    });

    menu.querySelectorAll("[data-dropdown-value]").forEach((item) => {
      item.addEventListener("click", (e) => {
        e.stopPropagation();
        const val = item.dataset.dropdownValue;
        const label = item.querySelector("span:last-child")?.textContent?.trim() ?? val;
        wrap.querySelector(".ap-dropdown__value").textContent = label;
        menu.querySelectorAll(".ap-dropdown__item").forEach((el) => {
          const on = el.dataset.dropdownValue === val;
          el.classList.toggle("ap-dropdown__item--on", on);
          el.setAttribute("aria-selected", on);
        });
        menu.classList.add("hidden");
        trigger.setAttribute("aria-expanded", "false");
        onChange?.({ wrap, value: val, label });
      });
    });
  });
}
